import { Outlet, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Masthead, Dropdown, LoadingIndicator } from "@uhg-harmony/react";
import { useAuth } from "react-oidc-context";
import type { AuthContextProps } from "react-oidc-context";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { clientsActions } from "../redux/clients/clientsSlice";
import type { Client } from "../redux/clients/clientsTypes";
import { selectTemplatesLoading } from "../redux/templates/templatesTreeSelectors";
import { templatesActions } from "../redux/templates";
import { useGetTemplates } from "../components/hooks/useGetTemplates";
import {
  setClient,
  setAccount,
  setClientId,
  setAccountId,
} from "../redux/metadata/metadataSlice";
import { selectUserRole } from "../redux/user/userSelectors";
import { isReadOnlyUser, getReadOnlyDropdownOption } from "../components/utils/roleHelpers";
import type {
  AccountOption,
  ClientOption,
} from "../types/optionTypes";
import type { UserRole } from "../redux/user/userTypes";
import "../components/sections/masthead-bar.css";
import { useNotificationsWebSocketConnection } from "../features/notifications/hooks/useNotificationsWebSocketConnection";
import { useECPNotifications } from "../features/notifications/hooks/useECPNotifications";
import { ToastProviderWrapper } from "../features/notifications/masthead/ToastProviderWrapper";
import { createLogger } from "../features/notifications/utils/logger";
import { NotificationModal } from "../features/notifications/modals/NotificationModal";
import { BaseTextInputModal } from "../components/modals/BaseTextInputModal";
import {
  SupportUploadModal,
  getSupportUploadMenuItem,
  isSupportUploadEnabled,
} from "../../support";
import { NOTIFICATION_FILTERS } from "../features/notifications/constants";
const logger = createLogger("AppLayout");

// Constants
const SELECT_ACCOUNT_PLACEHOLDER: AccountOption = {
  id: "0",
  label: "Select Account",
  value: "",
  title: "Select Account",
};

const SELECT_CLIENT_PLACEHOLDER: ClientOption = {
  id: "0",
  label: "Select Client",
  value: "",
  title: "Select Client",
};

const CREATE_NEW_CLIENT_OPTION: ClientOption = {
  id: "create-client",
  label: "Create Client",
  value: "__create_client__",
  title: "Create Client",
};

const CREATE_CLIENT_SUBMIT_EVENT = "create-client-submit";

function isCreateClientEnabled(flag?: string): boolean {
  if (!flag) return false;
  return ["y", "yes", "true", "1"].includes(flag.trim().toLowerCase());
}

type LocalCreatedClient = {
  id: string;
  account: string;
  option: ClientOption;
};

// Helper function to validate clients data with type guard
function isValidClientsData(data: unknown): data is Client[] {
  return !!(data && Array.isArray(data) && data.length > 0);
}

// Pure helper — no component state dependency
function canAutoSelectTemplateScope(
  role: UserRole,
  hasAccount: boolean,
  hasClient: boolean,
): boolean {
  if (isReadOnlyUser(role)) return false;
  // Account-only templates map to platform scope.
  if (hasAccount && !hasClient) {
    return role === "TB_ECP_ADMIN" || role === "TB_PLATFORM_ADMIN";
  }
  // Account+client templates map to client scope.
  if (hasAccount && hasClient) {
    return (
      role === "TB_ECP_ADMIN" ||
      role === "TB_PLATFORM_ADMIN" ||
      role === "TB_CLIENT_ADMIN"
    );
  }
  return false;
}

export default function AppLayout() {
  const auth = useAuth();
  const userProfile = auth.user?.profile;
  const usernameFromAuth =
    userProfile?.name ||
    userProfile?.preferred_username ||
    userProfile?.email ||
    "";
  const emailFromAuth = userProfile?.email || "";
  const token = auth.user?.access_token || "";
  const env = import.meta.env.VITE_ENV;
  const createClientOptionEnabled = isCreateClientEnabled(
    import.meta.env.VITE_CREATE_CLIENT_OPTION,
  );

  const dispatch = useAppDispatch();
  const clientsState = useAppSelector((state) => state.clients);
  const userRole = useAppSelector(selectUserRole);
  const isAuthSyncComplete = useAppSelector(
    (state) => state.user.isAuthSyncComplete,
  );
  const accountId = useAppSelector((state) => state.metadata.accountId);
  const clientId = useAppSelector((state) => state.metadata.clientId);
  const metadataAccount = useAppSelector((state) => state.metadata.account);
  const metadataClient = useAppSelector((state) => state.metadata.client);
  const metadataCategory = useAppSelector((state) => state.metadata.category);
  const templatesLoading = useAppSelector(selectTemplatesLoading);
  const templatesHasFetched = useAppSelector(
    (state) => state.templates.hasFetched,
  );
  const [initialTemplatesFetched, setInitialTemplatesFetched] = useState(false);
  const [isSupportUploadModalOpen, setIsSupportUploadModalOpen] = useState(false);
  const [isImageMetadataModalOpen, setIsImageMetadataModalOpen] = useState(false);
  const [isCreateClientModalOpen, setIsCreateClientModalOpen] = useState(false);
  const [localCreatedClients, setLocalCreatedClients] = useState<LocalCreatedClient[]>([]);
  const requestedClientsTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      requestedClientsTokenRef.current = null;
    }
  }, [token]);

  useEffect(() => {
    if (
      token &&
      isAuthSyncComplete &&
      !clientsState.hasFetched &&
      !clientsState.loading &&
      requestedClientsTokenRef.current !== token
    ) {
      requestedClientsTokenRef.current = token;
      const payload = { token };
      dispatch(clientsActions.getClientsByPlatformRequested(payload));
    }
  }, [
    dispatch,
    token,
    isAuthSyncComplete,
    clientsState.hasFetched,
    clientsState.loading,
  ]);

  useGetTemplates({
    platformId: accountId,
    clientId,
    token: auth.user?.access_token,
    enabled:
      !!auth.user?.access_token &&
      isAuthSyncComplete &&
      clientsState.hasFetched,
  });

  useEffect(() => {
    if (
      isAuthSyncComplete &&
      clientsState.hasFetched &&
      !templatesLoading &&
      templatesHasFetched &&
      !initialTemplatesFetched
    ) {
      setInitialTemplatesFetched(true);
    }
  }, [
    isAuthSyncComplete,
    clientsState.hasFetched,
    templatesLoading,
    templatesHasFetched,
    initialTemplatesFetched,
  ]);

  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(
    null,
  );
  const [selectedClients, setSelectedClients] = useState<ClientOption | null>(
    null,
  );
  const lastAppliedTemplateIntentRef = useRef<string>("");
  const [wsReady, setWsReady] = useState(false);
  const accountAutoSelectedRef = useRef<boolean>(false);
  const clientAutoSelectedRef = useRef<boolean>(false);

  // Memoize unique platforms to avoid repeated computation
  const uniquePlatforms = useMemo(() => {
    if (!isValidClientsData(clientsState.data)) {
      return [];
    }
    return Array.from(new Set(clientsState.data.map((item) => item.platform)));
  }, [clientsState.data]);

  const accounts: AccountOption[] = useMemo(() => {
    if (isReadOnlyUser(userRole)) {
      return [getReadOnlyDropdownOption()];
    }

    if (!isValidClientsData(clientsState.data)) {
      return [SELECT_ACCOUNT_PLACEHOLDER];
    }

    const platformOptions = uniquePlatforms.map((platform) => ({
      id: platform,
      label: platform,
      value: platform,
      title: platform,
    }));

    return [SELECT_ACCOUNT_PLACEHOLDER, ...platformOptions];
  }, [clientsState.data, userRole, uniquePlatforms]);

  // Auto-select account immediately when clientsState finishes loading
  useEffect(() => {
    if (
      !accountAutoSelectedRef.current &&
      clientsState.hasFetched &&
      !clientsState.loading &&
      accounts.length === 2 &&
      !selectedAccount &&
      userRole !== "TB_GENERAL_READONLY"
    ) {
      setSelectedAccount(accounts[1]);
      accountAutoSelectedRef.current = true;
    }
  }, [accounts, clientsState.hasFetched, clientsState.loading, selectedAccount, userRole]);

  const clients: ClientOption[] = useMemo(() => {
    if (isReadOnlyUser(userRole)) {
      return [getReadOnlyDropdownOption()];
    }

    if (!isValidClientsData(clientsState.data)) {
      return createClientOptionEnabled
        ? [SELECT_CLIENT_PLACEHOLDER, CREATE_NEW_CLIENT_OPTION]
        : [SELECT_CLIENT_PLACEHOLDER];
    }

    const hasMultipleAccounts = uniquePlatforms.length > 1;
    const selectedPlatform = selectedAccount?.value;
    const isValidPlatform = selectedPlatform && selectedPlatform !== "";

    if (hasMultipleAccounts && !isValidPlatform) {
      return createClientOptionEnabled
        ? [SELECT_CLIENT_PLACEHOLDER, CREATE_NEW_CLIENT_OPTION]
        : [SELECT_CLIENT_PLACEHOLDER];
    }

    const filtered = isValidPlatform
      ? clientsState.data.filter((item) => item.platform === selectedPlatform)
      : clientsState.data;

    // Collapse rows by client_id so one logical client ID is treated as one option.
    const uniqueClientRows = Array.from(
      new Map(filtered.map((item) => [String(item.client_id), item] as const)).values(),
    );

    const clientOptions = uniqueClientRows.map((item) => ({
      id: String(item.client_id),
      label: item.client_number || String(item.client_id),
      value: item.client_number || String(item.client_id),
      title: item.client_number || String(item.client_id),
    }));

    const localClientOptions = localCreatedClients
      .filter((item) => {
        if (!isValidPlatform) {
          return true;
        }
        return item.account === selectedPlatform;
      })
      .map((item) => item.option)
      .filter((option) => {
        return !clientOptions.some(
          (existing) => existing.value.toLowerCase() === option.value.toLowerCase(),
        );
      });

    return createClientOptionEnabled
      ? [
          SELECT_CLIENT_PLACEHOLDER,
          ...clientOptions,
          ...localClientOptions,
          CREATE_NEW_CLIENT_OPTION,
        ]
      : [
          SELECT_CLIENT_PLACEHOLDER,
          ...clientOptions,
          ...localClientOptions,
        ];
  }, [
    clientsState.data,
    createClientOptionEnabled,
    localCreatedClients,
    selectedAccount,
    userRole,
    uniquePlatforms,
  ]);

  // Reset selected client whenever account changes, so client selection is always account-scoped.
  useEffect(() => {
    clientAutoSelectedRef.current = false;
    setSelectedClients(null);
  }, [selectedAccount?.value]);

  // If exactly one account and one client exist, preselect the client once.
  useEffect(() => {
    const realClientOptions = clients.filter(
      (client) =>
        client.value !== "" &&
        client.value !== CREATE_NEW_CLIENT_OPTION.value,
    );

    if (
      !clientAutoSelectedRef.current &&
      accounts.length === 2 &&
      !!selectedAccount?.value &&
      realClientOptions.length === 1 &&
      !selectedClients &&
      userRole !== "TB_GENERAL_READONLY"
    ) {
      setSelectedClients(realClientOptions[0]);
      clientAutoSelectedRef.current = true;
    }
  }, [accounts, clients, selectedAccount, selectedClients, userRole]);

  useEffect(() => {
    if (!clientsState.hasFetched || !clientsState.data?.length) return;
    const clientsData = clientsState.data;

    const normalizedCategory = metadataCategory.trim().toUpperCase();
    const isGlobalTemplate =
      normalizedCategory === "GLOBAL" || normalizedCategory === "OPTUM";
    const desiredAccount = metadataAccount.trim();
    const desiredClient = metadataClient.trim();
    const hasDesiredAccount = !!desiredAccount;
    const hasDesiredClient = !!desiredClient;
    const intentKey = `${normalizedCategory}|${desiredAccount}|${desiredClient}`;

    // No metadata intent to apply.
    if (!hasDesiredAccount && !hasDesiredClient && !isGlobalTemplate) {
      lastAppliedTemplateIntentRef.current = "";
      return;
    }

    if (lastAppliedTemplateIntentRef.current === intentKey) {
      return;
    }

    if (isGlobalTemplate) {
      if (userRole !== "TB_ECP_ADMIN") return;
      // Only treat as explicit Global scope-reset when metadata has no
      // account/client intent. This avoids overriding manual dropdown picks.
      if (!hasDesiredAccount && !hasDesiredClient) {
        if (selectedClients !== null) {
          setSelectedClients(null);
        }
        if (selectedAccount !== null) {
          setSelectedAccount(null);
        }
      }
      lastAppliedTemplateIntentRef.current = intentKey;
      return;
    }

    // Ignore malformed metadata shape (client without account).
    if (!hasDesiredAccount && hasDesiredClient) return;

    if (!canAutoSelectTemplateScope(userRole, hasDesiredAccount, hasDesiredClient)) return;

    const equalsIgnoreCase = (a: string, b: string) =>
      a.trim().toLowerCase() === b.trim().toLowerCase();

    const resolveAccountValue = (rawAccount: string): string | undefined => {
      const byName = accounts.find(
        (option) => option.value && equalsIgnoreCase(option.value, rawAccount),
      );
      if (byName?.value) return byName.value;

      const byId = clientsData.find(
        (item) => String(item.platform_id).trim() === rawAccount.trim(),
      );
      return byId?.platform;
    };

    const resolveClientValue = (
      rawClient: string,
      selectedAccountValue?: string,
    ): string | undefined => {
      const byNumber = clients.find(
        (option) => option.value && equalsIgnoreCase(option.value, rawClient),
      );
      if (byNumber?.value) return byNumber.value;

      const byId = clientsData.find((item) => {
        const idMatches = String(item.client_id).trim() === rawClient.trim();
        if (!idMatches) return false;
        if (!selectedAccountValue) return true;
        return equalsIgnoreCase(item.platform, selectedAccountValue);
      });
      return byId?.client_number;
    };

    if (hasDesiredAccount) {
      const resolvedAccountValue = resolveAccountValue(desiredAccount);
      if (!resolvedAccountValue) return;

      const matchingAccount = accounts.find(
        (option) => option.value && equalsIgnoreCase(option.value, resolvedAccountValue),
      );

      if (!matchingAccount) return;

      if (selectedAccount?.value !== matchingAccount.value) {
        setSelectedAccount(matchingAccount);
        return;
      }

      // Account intent should keep account, but clear any selected client.
      if (!hasDesiredClient) {
        const hasSingleClientOption = clients.length === 2;
        if (!hasSingleClientOption && selectedClients !== null) {
          setSelectedClients(null);
          return;
        }
        lastAppliedTemplateIntentRef.current = intentKey;
        return;
      }
    }

    if (hasDesiredClient) {
      const accountValueForClientMatch = selectedAccount?.value;
      const resolvedClientValue = resolveClientValue(
        desiredClient,
        accountValueForClientMatch,
      );
      if (!resolvedClientValue) return;

      const matchingClient = clients.find(
        (option) => option.value && equalsIgnoreCase(option.value, resolvedClientValue),
      );

      if (!matchingClient) return;

      if (selectedClients?.value !== matchingClient.value) {
        setSelectedClients(matchingClient);
        return;
      }

      lastAppliedTemplateIntentRef.current = intentKey;
    }
  }, [
    accounts,
    clients,
    clientsState.data,
    clientsState.hasFetched,
    metadataCategory,
    metadataAccount,
    metadataClient,
    selectedAccount,
    selectedClients,
    userRole,
  ]);

  useEffect(() => {
    const normalizedCategory = metadataCategory.trim().toUpperCase();
    const isGlobalTemplate =
      normalizedCategory === "GLOBAL" || normalizedCategory === "OPTUM";

    if (isGlobalTemplate && !metadataAccount.trim() && !metadataClient.trim()) {
      return;
    }

    // Validate selected account still exists in accounts list
    if (selectedAccount && accounts.length > 1) {
      const accountStillExists = accounts.some(
        (acc) => acc.value === selectedAccount.value
      );
      if (!accountStillExists && selectedAccount.value !== "") {
        // Selected account no longer exists, reset to placeholder or auto-select if only one option
        if (accounts.length === 2) {
          setSelectedAccount(accounts[1]);
        } else {
          setSelectedAccount(null);
          accountAutoSelectedRef.current = false; // Reset flag so account can auto-select again
        }
      }
    }
  }, [
    accounts,
    metadataAccount,
    metadataCategory,
    metadataClient,
    selectedAccount,
  ]);

  const subscriptionVariable = useMemo(
    () => {
      const baseFilters: string[] = [NOTIFICATION_FILTERS.TEMPLATE_FILTERS];
      const selectedAccountFilter = selectedAccount?.value?.trim() || "";
      const accountFilters: string[] = userRole === "TB_PLATFORM_ADMIN" || userRole === "TB_CLIENT_ADMIN"
        ? (selectedAccountFilter ? [selectedAccountFilter] : [])
        : [];

      let filters: string[] = baseFilters;

      if (userRole === "TB_PLATFORM_ADMIN" || userRole === "TB_CLIENT_ADMIN") {
        filters = [...accountFilters];

      } else if (userRole === "TB_ECP_ADMIN" || userRole === "TB_GENERAL_READONLY") {
        filters = baseFilters;
      }


      filters = Array.from(new Set(filters));

      sessionStorage.setItem("notificationFilters", JSON.stringify(filters));

      return {
        recipientIdAttribute: null,
        recipientIdpAttribute: null,
        filters,
      };
    },
    [userRole, selectedAccount?.value],
  );

  const requiresAccountScopedConnection =
    userRole === "TB_PLATFORM_ADMIN" || userRole === "TB_CLIENT_ADMIN";
  const hasResolvedAccountForConnection =
    !requiresAccountScopedConnection || !!selectedAccount?.value;

  const isWebSocketReady =
    isAuthSyncComplete &&
    clientsState.hasFetched &&
    !clientsState.loading &&
    !!userRole &&
    (subscriptionVariable.filters?.length ?? 0) > 0 &&
    hasResolvedAccountForConnection;

  useNotificationsWebSocketConnection({
    token: token || undefined,
    env,
    subscriptionVariable,
    setWsReady,
    enabled: isWebSocketReady,
  });

  const ecpNotifications = useECPNotifications({
    token,
    env,
    wsReady,
    initialDays: 30,
  });

  const openSupportUploadModal = useCallback(() => {
    setIsSupportUploadModalOpen(true);
  }, []);

  const openImageMetadataModal = useCallback(() => {
    setIsImageMetadataModalOpen(true);
  }, []);

  const handleClientSelection = useCallback((value: ClientOption) => {
    if (value?.value === CREATE_NEW_CLIENT_OPTION.value) {
      setIsCreateClientModalOpen(true);
      return;
    }
    setSelectedClients(value);
  }, []);

  const handleCreateClientSubmit = useCallback((clientName: string) => {
    const normalizedName = clientName.trim();
    if (!normalizedName) {
      return;
    }

    const selectedAccountValue = selectedAccount?.value || "";
    const generatedId = `new-client-${Date.now()}`;
    const newClientOption: ClientOption = {
      id: generatedId,
      label: normalizedName,
      value: normalizedName,
      title: normalizedName,
    };

    const dummySuccessResponse = {
      status: true,
      message: "Client created successfully",
      account: selectedAccountValue,
      client: {
        clientId: generatedId,
        clientName: normalizedName,
      },
    };

    const detail = {
      account: selectedAccountValue,
      clientName: normalizedName,
      response: dummySuccessResponse,
    };

    window.dispatchEvent(
      new CustomEvent(CREATE_CLIENT_SUBMIT_EVENT, { detail }),
    );

    setLocalCreatedClients((previous) => {
      const exists = previous.some(
        (item) =>
          item.account === selectedAccountValue &&
          item.option.value.toLowerCase() === normalizedName.toLowerCase(),
      );
      if (exists) {
        return previous;
      }

      return [
        ...previous,
        {
          id: generatedId,
          account: selectedAccountValue,
          option: newClientOption,
        },
      ];
    });

    setSelectedClients(newClientOption);

    // Dummy create flow: trigger a templates refresh so the tree updates
    // immediately in the current account scope.
    if (token) {
      dispatch(
        templatesActions.getTemplatesRequested({
          platformId: "",
          clientId: "",
          templateCategory: "OPTUM",
          token,
        }),
      );

      if (accountId) {
        dispatch(
          templatesActions.getTemplatesRequested({
            platformId: accountId,
            clientId: "",
            templateCategory: "PLATFORM",
            token,
          }),
        );
      }
    }

    // Placeholder for backend wiring once create-client API is available.
    logger.info("Create Client submitted", detail);
  }, [accountId, dispatch, selectedAccount?.value, token]);

  const validateCreateClientName = useCallback((clientName: string): string | null => {
    if (!clientName) {
      return "Client name is required.";
    }

    const value = clientName;
    if (value !== value.trim()) {
      return "Client name must not start or end with spaces.";
    }

    if (value.length < 3 || value.length > 12) {
      return "Client name must be between 3 and 12 characters.";
    }

    const clientNamePattern = /^[A-Za-z0-9_-]+(?: [A-Za-z0-9_-]+)*$/;
    if (!clientNamePattern.test(value)) {
      return "Allowed: letters, numbers, underscore (_), hyphen (-), and a single space between words.";
    }

    return null;
  }, []);

  const mastheadProps = useMastheadProps({
    usernameFromAuth,
    emailFromAuth,
    auth,
    token,
    env,
    onSupportUploadClick: openSupportUploadModal,
    onGetImageMetadataClick: openImageMetadataModal,
    notificationMenuValues: ecpNotifications,
    accounts,
    selectedAccount,
    setSelectedAccount,
    clients,
    selectedClients,
    setSelectedClients: handleClientSelection,
    userRole,
    clientsLoading: clientsState.loading,
  });

  // Handle account selection and update Redux
  useEffect(() => {
    if (!selectedAccount?.value || selectedAccount.value === "") {
      dispatch(setAccount({ account: "" }));
      dispatch(setAccountId({ accountId: "" }));
      return;
    }

    if (!clientsState.data) return;

    const matchingPlatform = clientsState.data.find(
      (client) => client.platform === selectedAccount.value,
    );
    if (!matchingPlatform) return;

    const platformIdAcctId = String(matchingPlatform.platform_id);
    dispatch(setAccount({ account: selectedAccount.value }));
    dispatch(setAccountId({ accountId: platformIdAcctId }));
  }, [selectedAccount, clientsState.data, dispatch]);

  // Handle client selection and update Redux
  useEffect(() => {
    if (!selectedClients?.value || selectedClients.value === "") {
      dispatch(setClient({ client: "" }));
      dispatch(setClientId({ clientId: "" }));
      return;
    }

    if (selectedClients.value === CREATE_NEW_CLIENT_OPTION.value) {
      dispatch(setClient({ client: "" }));
      dispatch(setClientId({ clientId: "" }));
      return;
    }

    if (!selectedAccount?.value || !clientsState.data) return;

    const matchingClient = clientsState.data.find(
      (client) =>
        client.client_number === selectedClients.value &&
        client.platform === selectedAccount.value,
    );
    if (!matchingClient) {
      if (selectedClients.id.startsWith("new-client-")) {
        // Local dummy client: keep display value but do not set a synthetic
        // clientId in global metadata, otherwise downstream data fetch flows
        // may treat it as a real backend client and cause hard failures.
        dispatch(setClient({ client: selectedClients.value }));
        dispatch(setClientId({ clientId: "" }));
      }
      return;
    }

    const clientIdValue = String(matchingClient.client_id);
    dispatch(setClient({ client: selectedClients.value }));
    dispatch(setClientId({ clientId: clientIdValue }));
  }, [selectedClients, selectedAccount, clientsState.data, dispatch]);

  const resetAccountContext = useCallback(() => {
    setSelectedAccount(null);
    setSelectedClients(null);
    accountAutoSelectedRef.current = false;
    clientAutoSelectedRef.current = false;
  }, []);

  // Add title attributes to dropdown items for native tooltips on hover
  useEffect(() => {
    const targetText = CREATE_NEW_CLIENT_OPTION.label.toLowerCase();

    const styleDropdownItems = () => {
      const dropdownItems = document.querySelectorAll('[role="option"], [role="menuitem"], li');
      dropdownItems.forEach((item) => {
        const textContent = item.textContent?.trim() || "";
        if (textContent && !item.getAttribute("title")) {
          item.setAttribute("title", textContent);
        }

        const el = item as HTMLElement;
        if (textContent.toLowerCase() === targetText) {
          el.classList.add("create-client-option");
        } else {
          el.classList.remove("create-client-option");
        }
      });
    };

    styleDropdownItems();
    const id = requestAnimationFrame(styleDropdownItems);

    const observer = new MutationObserver(() => {
      styleDropdownItems();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    return () => {
      cancelAnimationFrame(id);
      observer.disconnect();
    };
  }, [accounts, clients]);

  useEffect(() => {
    const handleCreateClientSubmitEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ account: string; clientName: string }>;
      console.log("Create Client submit event", customEvent.detail);
    };

    window.addEventListener(
      CREATE_CLIENT_SUBMIT_EVENT,
      handleCreateClientSubmitEvent,
    );

    return () => {
      window.removeEventListener(
        CREATE_CLIENT_SUBMIT_EVENT,
        handleCreateClientSubmitEvent,
      );
    };
  }, []);

  const isLoadingClients = clientsState.loading && !clientsState.data;
  const isLoadingTemplates =
    isAuthSyncComplete && clientsState.hasFetched && !initialTemplatesFetched;
  const showLoading = isLoadingClients || isLoadingTemplates;

  return (
    <>
      <Masthead {...mastheadProps} />
      <ToastProviderWrapper>
        <div>
          {showLoading ? (
            <LoadingIndicator
              loading
              loadingText="Loading..."
              centerSpinner
              displayOverlay={false}
              accessibilityText="Loading application"
            />
          ) : (
            <Outlet context={{ resetAccountContext }} />
          )}
        </div>
      </ToastProviderWrapper>
      <SupportUploadModal
        title="Upload Image"
        open={isSupportUploadModalOpen}
        onClose={() => setIsSupportUploadModalOpen(false)}
        token={token}
      />
      <BaseTextInputModal
        title="Create Client"
        open={isCreateClientModalOpen}
        onClose={() => setIsCreateClientModalOpen(false)}
        onConfirm={handleCreateClientSubmit}
        validateInput={validateCreateClientName}
        inputLabel="Client Name"
        confirmButtonText="Submit"
        placeholder="3-12 chars: letters/numbers/_/- and one space between words"
      />
      <SupportUploadModal
        title="getImageMetaData"
        open={isImageMetadataModalOpen}
        onClose={() => setIsImageMetadataModalOpen(false)}
        token={token}
        mode="metadata"
      />
      <NotificationModal />
    </>
  );
}

function useMastheadProps({
  usernameFromAuth,
  emailFromAuth,
  auth,
  token,
  env,
  onSupportUploadClick,
  onGetImageMetadataClick,
  notificationMenuValues,
  accounts,
  selectedAccount,
  setSelectedAccount,
  clients,
  selectedClients,
  setSelectedClients,
  userRole,
  clientsLoading,
}: {
  usernameFromAuth: string;
  emailFromAuth: string;
  auth: AuthContextProps;
  token: string;
  env: string;
  onSupportUploadClick: () => void;
  onGetImageMetadataClick: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notificationMenuValues: any; // Typed as any: MastheadNotificationsResult is incompatible with Masthead's internal ECPNotificationItem type
  accounts: AccountOption[];
  selectedAccount: AccountOption | null;
  setSelectedAccount: (value: AccountOption) => void;
  clients: ClientOption[];
  selectedClients: ClientOption | null;
  setSelectedClients: (value: ClientOption) => void;
  userRole: UserRole;
  clientsLoading: boolean;
}) {
  const navigate = useNavigate();
  const hasSingleAccountOption = accounts.length === 2;
  const singleAccountValue = hasSingleAccountOption ? accounts[1]?.value : "";
  const isSingleAccountDefaultSelected =
    hasSingleAccountOption &&
    !!singleAccountValue &&
    selectedAccount?.value === singleAccountValue;
  const supportUploadEnabled = isSupportUploadEnabled(env);

  return useMemo(
    () => ({
      productSwitcherValues: {
        currentProductId: "1",
        products: [
          {
            clickHandler: () => { },
            id: "1",
            name: "Template Studio",
          },
        ],
      },
      clientSwitcherValues: {
        ClientSwitcherOverride: () => (
          <div className="masthead-dropdowns">
            <div className="dropdown-item">
              <span className="dropdown-label">Account:</span>
              <Dropdown
                className="borderless-dropdown"
                type="single"
                items={accounts}
                value={selectedAccount}
                onChange={setSelectedAccount}
                showClearButton={false}
                placeholderText="Select Account"
                disabled={
                  isReadOnlyUser(userRole) ||
                  clientsLoading ||
                  isSingleAccountDefaultSelected
                }
              />
            </div>

            <div className="dropdown-item">
              <span className="dropdown-label">Client:</span>
              <Dropdown
                className="borderless-dropdown"
                type="single"
                items={clients}
                value={selectedClients}
                onChange={setSelectedClients}
                showClearButton={false}
                placeholderText="Select Client"
                disabled={isReadOnlyUser(userRole) || clientsLoading || clients.length <= 1 || !selectedAccount?.value}
              />
            </div>
          </div>
        ),
      },
      userMenuValues: {
        displayName: usernameFromAuth,
        initials: getInitials(usernameFromAuth),
        email: emailFromAuth,
        menuItems: [
          ...(supportUploadEnabled ? [getSupportUploadMenuItem(onSupportUploadClick)] : []),
          {
            id: "getImageMetaData",
            label: "getImageMetaData",
            clickHandler: onGetImageMetadataClick,
          },

          ...(window.location.hostname.includes("local") ||
            window.location.hostname.includes("dev")
            ? [
              {
                id: "profile",
                label: "Copy User Token",
                clickHandler: () => {
                  navigator.clipboard.writeText(token);
                },
              },
            ]
            : []),
        ],
        onSignOut: () => {
          auth
            .signoutRedirect({
              post_logout_redirect_uri: `${window.location.origin}/logout`,
            })
            .catch((err: unknown) => logger.error("Logout failed:", err));
        },
      },
      notificationMenuValues,
    }),
    [
      auth,
      emailFromAuth,
      notificationMenuValues,
      usernameFromAuth,
      navigate,
      accounts,
      selectedAccount,
      setSelectedAccount,
      clients,
      selectedClients,
      setSelectedClients,
      userRole,
      clientsLoading,
      isSingleAccountDefaultSelected,
      onSupportUploadClick,
      onGetImageMetadataClick,
      supportUploadEnabled,
    ],
  );
}

function getInitials(fullName = "") {
  return fullName
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .filter(Boolean)
    .join("")
    .toUpperCase();
}
